'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useCookieConsent, type ConsentPreferences } from '@/lib/hooks/useCookieConsent';
import { Cookie, Shield, Settings, X, Check } from 'lucide-react';
import { cn } from '@/lib/utils/helpers';

export default function CookieConsent() {
  const {
    showBanner,
    preferences,
    acceptAll,
    rejectAll,
    savePreferences,
  } = useCookieConsent();

  const [showSettings, setShowSettings] = useState(false);
  const [customPreferences, setCustomPreferences] = useState<ConsentPreferences>(preferences);

  if (!showBanner) return null;

  const handleSaveCustom = () => {
    savePreferences(customPreferences);
    setShowSettings(false);
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998]" />

      {/* Main Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-slate-900 border border-white/10 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden">
            {!showSettings ? (
              // Simple view
              <div className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-amber-600/10 flex-shrink-0">
                    <Cookie className="w-6 h-6 text-amber-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-semibold text-white mb-2">
                      We value your privacy
                    </h2>
                    <p className="text-slate-400 text-sm leading-relaxed mb-4">
                      We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
                      By clicking &quot;Accept All&quot;, you consent to our use of cookies as described in our{' '}
                      <Link
                        href="/privacypolicy"
                        className="text-amber-500 hover:text-amber-400 underline"
                      >
                        Privacy Policy
                      </Link>
                      .
                    </p>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={acceptAll}
                        className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-medium transition-all"
                      >
                        <Check className="w-4 h-4" />
                        Accept All
                      </button>
                      <button
                        onClick={rejectAll}
                        className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium transition-all"
                      >
                        <X className="w-4 h-4" />
                        Reject All
                      </button>
                      <button
                        onClick={() => setShowSettings(true)}
                        className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 font-medium transition-all"
                      >
                        <Settings className="w-4 h-4" />
                        Customize
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Settings view
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-600/10">
                      <Shield className="w-5 h-5 text-amber-500" />
                    </div>
                    <h2 className="text-lg font-semibold text-white">
                      Cookie Preferences
                    </h2>
                  </div>
                  <button
                    onClick={() => setShowSettings(false)}
                    className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4 mb-6">
                  {/* Necessary Cookies */}
                  <CookieToggle
                    title="Strictly Necessary"
                    description="These cookies are essential for the website to function properly. They cannot be disabled."
                    checked={true}
                    disabled={true}
                    onChange={() => {}}
                  />

                  {/* Analytics Cookies */}
                  <CookieToggle
                    title="Analytics & Performance"
                    description="These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously."
                    checked={customPreferences.analytics}
                    onChange={(checked) =>
                      setCustomPreferences((prev) => ({ ...prev, analytics: checked }))
                    }
                  />

                  {/* Marketing Cookies */}
                  <CookieToggle
                    title="Marketing & Advertising"
                    description="These cookies are used to track visitors across websites to display relevant advertisements."
                    checked={customPreferences.marketing}
                    onChange={(checked) =>
                      setCustomPreferences((prev) => ({ ...prev, marketing: checked }))
                    }
                  />

                  {/* Preferences Cookies */}
                  <CookieToggle
                    title="Preferences & Functionality"
                    description="These cookies allow the website to remember choices you make and provide enhanced features."
                    checked={customPreferences.preferences}
                    onChange={(checked) =>
                      setCustomPreferences((prev) => ({ ...prev, preferences: checked }))
                    }
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-white/10">
                  <button
                    onClick={handleSaveCustom}
                    className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-medium transition-all"
                  >
                    <Check className="w-4 h-4" />
                    Save Preferences
                  </button>
                  <button
                    onClick={acceptAll}
                    className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium transition-all"
                  >
                    Accept All
                  </button>
                </div>

                <p className="text-xs text-slate-500 mt-4">
                  For more information about how we use cookies, please see our{' '}
                  <Link
                    href="/privacypolicy"
                    className="text-amber-500 hover:text-amber-400 underline"
                  >
                    Privacy Policy
                  </Link>
                  .
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

interface CookieToggleProps {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}

function CookieToggle({ title, description, checked, disabled, onChange }: CookieToggleProps) {
  return (
    <div className="flex items-start gap-4 p-4 rounded-xl bg-white/5 border border-white/5">
      <div className="flex-1">
        <h3 className="text-white font-medium mb-1">{title}</h3>
        <p className="text-slate-400 text-sm">{description}</p>
      </div>
      <button
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={cn(
          'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out',
          checked ? 'bg-amber-600' : 'bg-slate-700',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        <span
          className={cn(
            'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
            checked ? 'translate-x-5' : 'translate-x-0.5',
            'mt-0.5'
          )}
        />
      </button>
    </div>
  );
}

